import { useCallback, useEffect, useState } from 'react'
import Sheet from './Sheet.jsx'
import Attachments from './Attachments.jsx'
import Links from './Links.jsx'
import { supabase } from '../lib/supabase.js'
import { addProjectItem, resolveLinks, getLinks, setItemDone } from '../lib/notes.js'
import { shortDate } from '../lib/format.js'

/**
 * A project, opened from the calendar.
 *
 * The money leads. On a trip "how much have I raised" is the question, and
 * the answer lives on the build the project is linked to — so it is read
 * from there rather than duplicated onto the project, which would be two
 * numbers to keep in step.
 */
export default function ProjectSheet({ project, toast, onClose, onChanged }) {
  const [items, setItems] = useState([])
  const [links, setLinks] = useState([])
  const [dates, setDates] = useState({
    starts_on: project.starts_on ?? '',
    ends_on: project.ends_on ?? '',
  })
  const [editing, setEditing] = useState(false)
  const [adding, setAdding] = useState('')

  const load = useCallback(async () => {
    const [it, lk] = await Promise.all([
      supabase.from('project_items').select('*').eq('project_id', project.id).order('sort_order'),
      getLinks('project', project.id).then(resolveLinks).catch(() => []),
    ])
    setItems(it.data ?? [])
    setLinks(lk)
  }, [project.id])

  useEffect(() => { load() }, [load])

  /* The linked build is the money. One of them, not a sum: a project raising
     into two builds would be a different question and he has not asked it. */
  const build = links.find((l) => l.type === 'build')

  const open = items.filter((i) => i.status !== 'Done')
  const done = items.filter((i) => i.status === 'Done')

  async function saveDates() {
    try {
      await supabase.from('projects').update({
        starts_on: dates.starts_on || null,
        ends_on: dates.ends_on || null,
      }).eq('id', project.id)
      setEditing(false)
      onChanged?.()
    } catch (e) { toast?.(e.message || 'Could not save those dates.') }
  }

  return (
    <Sheet title={project.title} onClose={onClose}>
      {editing ? (
        <>
          <div className="two">
            <div className="field">
              <label className="lbl" htmlFor="pj-from">Starts</label>
              <input id="pj-from" type="date" value={dates.starts_on}
                onChange={(e) => setDates((d) => ({ ...d, starts_on: e.target.value }))} />
            </div>
            <div className="field">
              <label className="lbl" htmlFor="pj-to">Ends</label>
              <input id="pj-to" type="date" value={dates.ends_on}
                onChange={(e) => setDates((d) => ({ ...d, ends_on: e.target.value }))} />
            </div>
          </div>
          <p className="hint" style={{ margin: '0 0 8px' }}>
            Leave both empty and it stays off the calendar. Most projects are
            not events.
          </p>
          <div className="sheet-actions">
            <button type="button" className="btn ghost" onClick={() => setEditing(false)}>Cancel</button>
            <button type="button" className="btn" onClick={saveDates}>Save dates</button>
          </div>
        </>
      ) : (
        <button type="button" className="linkish" style={{ marginBottom: 12 }}
                onClick={() => setEditing(true)}>
          {project.starts_on
            ? span(project.starts_on, project.ends_on) + ' · edit'
            : 'Add dates'}
        </button>
      )}

      {build ? (
        <div className="pj-fund">
          <p className="lbl" style={{ margin: '0 0 4px' }}>{build.label}</p>
          <div className="v num">{build.detail}</div>
        </div>
      ) : null}

      <div className="rows" style={{ marginTop: 4 }}>
        {open.map((i) => (
          <div className="row" key={i.id}>
            <button type="button" className="tick" aria-label="Mark done"
              onClick={async () => { await setItemDone(i, true); load() }}>✓</button>
            <span className="nm"><b>{i.body}</b></span>
          </div>
        ))}
        {done.length ? (
          <p className="lbl" style={{ padding: '9px 0 0' }}>
            {done.length} done
          </p>
        ) : null}
      </div>

      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (!adding.trim()) return
          const body = adding.trim()
          setAdding('')
          try {
            await addProjectItem({ projectId: project.id, body, sortOrder: items.length })
            load()
          } catch (err) { toast?.(err.message || 'Could not add that.') }
        }}
      >
        <div className="field">
          <input value={adding} onChange={(e) => setAdding(e.target.value)}
            placeholder="Add an item" autoComplete="off" />
        </div>
      </form>

      <Links target={{ type: 'project', id: project.id }} toast={toast} compact />
      <Attachments target={{ type: 'project', id: project.id }} toast={toast} compact />
    </Sheet>
  )
}

function span(from, to) {
  if (!to || to === from) return shortDate(from)
  return shortDate(from) + ' – ' + shortDate(to)
}
